import React, { useState, useContext, useMemo } from "react";
import "./Contact.css";
import { FiMapPin, FiMail, FiPhone } from "react-icons/fi";
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaYoutube } from "react-icons/fa";
import { MyContext } from "../Context/MyContext";
import { ContentPageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { useToast } from "../Components/Toast/ToastProvider";
import { contactApi } from "../../services/api";

export default function Contact() {
  const { contact } = useContext(MyContext);
  const { showToast } = useToast();
  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const panel = contact.panel || {};
  const formConfig = contact.form || {};
  const validation = formConfig.validation || {};

  const visibleFields = useMemo(
    () => (formConfig.fields || []).filter((f) => f.is_show !== false),
    [formConfig.fields]
  );

  const handleChange = (name, value) => {
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    visibleFields.forEach((field) => {
      if (field.required) {
        const value = formData[field.name];
        if (value === undefined || value === null || String(value).trim() === "") {
          errors[field.name] = `${field.label} is required`;
        }
      }
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      showToast({
        type: "error",
        title: "Validation Failed",
        message: validation.requiredMessage,
      });
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      await contactApi.submit(formData);
      showToast({
        type: "success",
        title: validation.successTitle,
        message: validation.successMessage,
      });
      setFormData({});
    } catch (err) {
      showToast({
        type: "error",
        title: validation.errorTitle,
        message: err.message || validation.errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const inputClass = fieldErrors[field.name] ? "input-error" : "";

    if (field.type === "textarea") {
      return (
        <textarea
          placeholder={field.placeholder || field.label}
          value={formData[field.name] || ""}
          className={inputClass}
          rows={field.rows || 5}
          onChange={(e) => handleChange(field.name, e.target.value)}
        />
      );
    }

    if (field.type === "select") {
      return (
        <select
          value={formData[field.name] || ""}
          className={inputClass}
          onChange={(e) => handleChange(field.name, e.target.value)}
        >
          <option value="">{field.placeholder || `Select ${field.label}`}</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type || "text"}
        placeholder={field.placeholder || field.label}
        value={formData[field.name] || ""}
        className={inputClass}
        onChange={(e) => handleChange(field.name, e.target.value)}
      />
    );
  };

  const renderFieldGroups = () => {
    const elements = [];
    let halfBuffer = [];

    const flushHalfRow = () => {
      if (!halfBuffer.length) return;
      elements.push(
        <div className="form-row" key={`row-${elements.length}`}>
          {halfBuffer.map((field) => (
            <div
              key={field.name}
              className={`contact-field ${fieldErrors[field.name] ? "has-error" : ""}`}
            >
              <label className="contact-label">
                {field.label}
                {field.required && <span className="required-mark">*</span>}
              </label>
              {renderField(field)}
              {fieldErrors[field.name] && (
                <span className="field-error">{fieldErrors[field.name]}</span>
              )}
            </div>
          ))}
        </div>
      );
      halfBuffer = [];
    };

    visibleFields.forEach((field) => {
      if (field.layout === "half") {
        halfBuffer.push(field);
        if (halfBuffer.length === 2) flushHalfRow();
      } else {
        flushHalfRow();
        elements.push(
          <div
            key={field.name}
            className={`contact-field full ${fieldErrors[field.name] ? "has-error" : ""}`}
          >
            <label className="contact-label">
              {field.label}
              {field.required && <span className="required-mark">*</span>}
            </label>
            {renderField(field)}
            {fieldErrors[field.name] && (
              <span className="field-error">{fieldErrors[field.name]}</span>
            )}
          </div>
        );
      }
    });

    flushHalfRow();
    return elements;
  };

  return (
    <PageLoadGate skeleton={<ContentPageSkeleton />} delay={380}>
      <div className="contact-page">
        <div className="contact-hero">
          <div className="contact-hero-overlay">
            <h1>{contact.hero.title}</h1>
            <p>{contact.hero.subtitle}</p>
          </div>
        </div>

        <div className="contact-wrapper">
          <div className="contact-card">
            <div className="contact-left">
              <h2>{panel.title}</h2>
              <p className="contact-subtitle">{panel.subtitle}</p>

              <div className="contact-item">
                <div className="icon">
                  <FiMapPin />
                </div>
                <div>
                  <b>{contact.office.name}</b>
                  <p>{contact.office.address}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="icon">
                  <FiMail />
                </div>
                <div>
                  <b>{panel.emailLabel}</b>
                  <p>{contact.email}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="icon">
                  <FiPhone />
                </div>
                <div>
                  <b>{panel.phoneLabel}</b>
                  <p>{contact.phone}</p>
                </div>
              </div>

              <div className="socials">
                <span>{panel.socialLabel}</span>
                <div className="social-icons">
                  <a href={contact.socials.facebook} className="icon facebook">
                    <FaFacebookF />
                  </a>
                  <a href={contact.socials.twitter} className="icon twitter">
                    <FaTwitter />
                  </a>
                  <a href={contact.socials.linkedin} className="icon linkedin">
                    <FaLinkedinIn />
                  </a>
                  <a href={contact.socials.youtube} className="icon youtube">
                    <FaYoutube />
                  </a>
                </div>
              </div>
            </div>

            <form className="contact-right" onSubmit={handleSubmit} noValidate>
              <h2>{formConfig.title}</h2>
              {renderFieldGroups()}
              <button className="send-btn" type="submit" disabled={submitting}>
                {submitting ? formConfig.submittingLabel : formConfig.submitLabel}
              </button>
            </form>
          </div>
        </div>

        <div className="map-section">
          <iframe
            title="map"
            src={`https://maps.google.com/maps?q=${contact.map.location}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
            loading="lazy"
          />
        </div>
      </div>
    </PageLoadGate>
  );
}
